import { PencilIcon, PlusCircleIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { GraphQLError } from 'graphql'
import React, { useEffect } from 'react'

import {
  Location,
  Maybe,
  RequestForQuote,
  RequestForQuoteStep,
  useSearchInventoryQuery,
  useFleetioInventoryInfoQuery,
  useClueInventoryInfoQuery,
  OrgMachine,
} from '@/buyers/_gen/gql'
import { PartRequest, ShowMore } from '../../types'

import Action from '@/gf/components/Action'
import Checkbox from '@/gf/components/Checkbox'
import LinkButton from '@/gf/components/LinkButtonOld'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { UNKNOWN_ERROR } from '@/gf/providers/MsgsProvider'
import useDeletePartRequests from '../hooks/useDeletePartRequests'
import AddPartModal from './AddPartModal'
import EditPartModal from './EditPartModal'
import useSession from '../hooks/useSession'
import JobNumbersAction from './PartRequestsTableV2/JobNumbersAction'
import CostCodeAction from './PartRequestsTableV2/CostCodeAction'
import PhaseCodeAction from './PartRequestsTableV2/PhaseCodeAction'
import PartNumberAction from './PartRequestsTableV2/PartNumberAction'
import Spinner from '@/gf/components/Spinner'
import useGqlClient from '../hooks/useGqlClient'
import MoneyM from '@/gf/modules/Money'
import JobCodeM from '@/gf/modules/JobCode'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/gf/components/next/Tooltip'
import { isRfqClosed } from '@/buyers/modules/RfqClosed'

const Description = ({ text }: { text: string }) => (
  <span className={classNames('text-sm', !text && 'text-slate-400 italic')}>
    {text || 'No description provided.'}
  </span>
)

const NoMatchingInventory = ({
  mpn,
  location,
}: {
  mpn: string | undefined | null
  location: { name: string; code: string | undefined | null } | undefined | null
}) =>
  !mpn ? (
    <p>Need the part number to search inventory.</p>
  ) : !location?.code ? (
    <>
      <p>Need the location code to search inventory.</p>
      {location && (
        <p>
          <span className="font-medium">{location.name}</span> doesn&apos;t have a code.
        </p>
      )}
    </>
  ) : (
    <p>
      No available inventory for part <span className="font-medium">{mpn}</span> at location{' '}
      <span className="font-medium">{location.code}</span>.
    </p>
  )

const PartRequestsTableV2 = ({
  rfq,
  onChange,
  viewOnly = false,
  maxRows,
  showMore = ShowMore.None,
  onShowMoreChange,
}: {
  rfq: Pick<
    RequestForQuote,
    | 'id'
    | 'step'
    | 'partRequests'
    | 'closed'
    | 'enabled'
    | 'closedAt'
    | 'canceledAt'
    | 'quotesApproved'
    | 'cluePollingEnabled'
    | 'workOrderNumber'
  > & {
    shippingLocation: Maybe<Pick<Location, 'id' | 'name' | 'code'>>
  } & { orgMachines: Array<Pick<OrgMachine, 'fleetioId'>> }
  onChange?: () => void
  viewOnly?: boolean
  maxRows?: number | undefined
  showMore?: ShowMore
  onShowMoreChange?: (newShowMore: ShowMore) => void
}) => {
  const { organization, featureFlags, features } = useSession()
  const [_, msgr] = useMsgs()
  const [selection, setSelection] = React.useState<string[]>([])
  const [deleting, setDeleting] = React.useState(false)
  const check = (id: string) => setSelection([...selection, id])
  const unCheck = (id: string) => setSelection(selection.filter((sid) => sid !== id))
  const deletePartRequests = useDeletePartRequests()
  const [adding, addingToggler] = useToggle(false)
  const [editingPartRequest, setEditingPartRequest] = React.useState<PartRequest>()
  const hasFleetioMachine = rfq.orgMachines.find((m) => m.fleetioId)
  const showRfqQty = !isRfqClosed(rfq) && !rfq.canceledAt && (rfq.quotesApproved ?? 0) > 0

  const client = useGqlClient()
  const {
    data: inventoryData,
    error: _inventoryError,
    refetch: inventoryRefetch,
  } = useSearchInventoryQuery({
    client,
    variables: { requestForQuoteId: rfq.id },
    skip: !featureFlags.assetworksApiPartInventory,
  })

  const {
    data: fleetioInventoryInfo,
    error: _fleetioInventoryInfoError,
    refetch: fleetioInventoryInfoRefetch,
  } = useFleetioInventoryInfoQuery({
    client,
    variables: { requestForQuoteId: rfq.id },
    skip: !hasFleetioMachine,
  })

  const {
    data: clueInventoryInfo,
    error: _clueInventoryInfoError,
    refetch: clueInventoryInfoRefetch,
  } = useClueInventoryInfoQuery({
    client,
    variables: { requestForQuoteId: rfq.id },
    skip: !rfq.cluePollingEnabled,
  })

  // Refetch the inventory query when the MPNs change
  useEffect(() => {
    inventoryRefetch()
    fleetioInventoryInfoRefetch()
    clueInventoryInfoRefetch()
  }, [rfq.partRequests.map((pr) => pr.mpn).join(',')])

  const onDelete = () => {
    if (deleting) return
    setDeleting(true)

    deletePartRequests({ rfqId: rfq.id, partRequestIds: selection })
      .then(() => {
        setSelection([])
        if (onChange) onChange()
        msgr.add('Part(s) deleted.', 'positive')
      })
      .catch((err: GraphQLError) => {
        msgr.add(err.message || UNKNOWN_ERROR, 'negative')
      })
      .finally(() => setDeleting(false))
  }

  const onAdded = () => {
    if (onChange) onChange()
    addingToggler.off()
  }

  const onEdited = () => {
    if (onChange) onChange()
    setEditingPartRequest(undefined)
  }

  const canChange =
    !viewOnly &&
    [
      RequestForQuoteStep.Submitted,
      RequestForQuoteStep.Inbound,
      RequestForQuoteStep.Fulfilling,
    ].includes(rfq.step)
  const canAdd = canChange
  const canEdit = canChange
  const canDelete = canChange && rfq.partRequests.length > 1

  const showJobNumbers = organization.requestsJobNumbersEnabled
  const showCostCode = organization.costCodesEnabled
  const showPhaseCode = features.phaseCodesEnabled

  // The M5 job code every part on this work order is charged to. Only AssetWorks orgs
  // have one, and only once the request carries a work order — without a WO there is no
  // job to pick. Distinct from `showJobNumbers`, which is the org's own job numbers
  // (GEA-6990).
  const showJobCode = !!featureFlags.assetworksApiIssuePartsToWorkOrder && !!rfq.workOrderNumber
  const jobCodeLabel = showJobNumbers ? 'Job Code (M5)' : 'Job Code'

  return (
    <>
      <AddPartModal
        rfqId={rfq.id}
        open={adding}
        onClose={addingToggler.off}
        onAdded={onAdded}
        jobCodeWorkOrderId={showJobCode ? (rfq.workOrderNumber ?? undefined) : undefined}
      />
      <EditPartModal
        partRequest={editingPartRequest}
        onClose={() => setEditingPartRequest(undefined)}
        onEdited={onEdited}
        showRfqQty={showRfqQty}
        useCluePartSearch={!!rfq.cluePollingEnabled}
        jobCodeWorkOrderId={showJobCode ? (rfq.workOrderNumber ?? undefined) : undefined}
      />
      <div className="space-y-2">
        {selection.length > 0 && rfq.partRequests.length !== selection.length && (
          <div className="flex gap-2 items-center text-sm">
            <div>{selection.length.toLocaleString()} selected:</div>
            <Action.S onClick={onDelete} size="sm" disabled={deleting}>
              {deleting ? <>Deleting&hellip;</> : <>Delete</>}
            </Action.S>
          </div>
        )}

        <div className="border rounded-lg">
          <table className="table-auto border-collapse w-full">
            <thead className="hidden md:table-header-group">
              <tr className="border-b text-sm">
                {canDelete && <th aria-label="Select to Delete" />}
                {showJobNumbers && (
                  <th className="text-left leading-4 align-middle font-medium text-slate-600 px-2 py-2">
                    Job Numbers
                  </th>
                )}
                {showCostCode && (
                  <th className="text-left leading-4 align-middle font-medium text-slate-600 px-2 py-2">
                    Cost Code
                  </th>
                )}
                {showPhaseCode && (
                  <th className="text-left leading-4 align-middle font-medium text-slate-600 px-2 py-2">
                    Phase Code
                  </th>
                )}
                <th className="text-left leading-4 align-middle font-medium text-slate-600 px-2 py-2">
                  Part Number
                </th>
                <th className="text-left align-middle font-medium text-slate-600 px-2 py-2 hidden md:table-cell">
                  Description
                </th>
                {showJobCode && (
                  <th className="text-left align-middle font-medium text-slate-600 px-2 py-2 hidden md:table-cell whitespace-nowrap">
                    {jobCodeLabel}
                  </th>
                )}
                <th className="text-left align-middle font-medium text-slate-600 px-2 py-2 hidden md:table-cell">
                  Qty
                </th>
                {showRfqQty && (
                  <th className="text-left align-middle font-medium text-slate-600 px-2 py-2 hidden md:table-cell">
                    RFQ Qty
                  </th>
                )}
                {featureFlags.assetworksApiPartInventory && (
                  <th className="text-left align-middle font-medium text-slate-600 px-2 py-2 hidden md:table-cell">
                    Inv Qty
                  </th>
                )}
                {hasFleetioMachine && (
                  <th className="text-left align-middle font-medium text-slate-600 px-2 py-2 hidden md:table-cell">
                    Fleetio Inv Qty
                  </th>
                )}
                {rfq.cluePollingEnabled && (
                  <th className="text-left align-middle font-medium text-slate-600 px-2 py-2 hidden md:table-cell">
                    Clue Inv Qty
                  </th>
                )}
                {rfq.cluePollingEnabled && (
                  <th className="text-left align-middle font-medium text-slate-600 px-2 py-2 hidden md:table-cell">
                    Clue Unit Cost
                  </th>
                )}
                {canEdit && <th aria-label="Click to Edit" />}
              </tr>
            </thead>
            <tbody>
              {rfq.partRequests
                .slice(0, typeof maxRows === 'undefined' ? undefined : maxRows)
                .map((pr) => {
                  const partInventory = inventoryData?.searchInventory.find(
                    ({ partRequestId }) => partRequestId === pr.id
                  )
                  const fleetioInfo =
                    fleetioInventoryInfo &&
                    fleetioInventoryInfo.fleetioInventoryInfo.find(({ id }) => id == pr.id)
                  const clueInfo =
                    clueInventoryInfo &&
                    clueInventoryInfo.clueInventoryInfo.find(({ id }) => id == pr.id)
                  return (
                    <tr key={pr.id} className="border-b last:border-0">
                      {canDelete && (
                        <td className="pl-2 align-middle py-1.5">
                          <Checkbox
                            color="blue"
                            checked={selection.includes(pr.id)}
                            setChecked={(checked) => (checked ? check(pr.id) : unCheck(pr.id))}
                          />
                        </td>
                      )}

                      {showJobNumbers && (
                        <td className="text-left md:font-medium px-2 py-1.5 align-middle">
                          <JobNumbersAction rfqPart={pr} />
                        </td>
                      )}
                      {showCostCode && (
                        <td className="text-left md:font-medium px-2 py-1.5 align-middle">
                          <CostCodeAction rfqPart={pr} />
                        </td>
                      )}
                      {showPhaseCode && (
                        <td className="text-left md:font-medium px-2 py-1.5 align-middle">
                          <PhaseCodeAction rfqPart={pr} />
                        </td>
                      )}

                      <td className="text-left md:font-medium px-2 py-1.5 align-middle">
                        <span className="hidden md:inline-block text-sm">
                          {featureFlags.assetworksApiSelectPart && canEdit ? (
                            <PartNumberAction rfqPart={pr} />
                          ) : (
                            pr.mpn
                          )}
                        </span>
                        <div className="md:hidden flex items-center">
                          <span className="flex-grow flex flex-col">
                            <span className="font-medium">
                              <span className="text-slate-600">Part #:</span> {pr.mpn}
                            </span>
                            <Description text={pr.description} />
                            {showJobCode && (
                              <span className="text-sm">
                                <span className="text-slate-600">{jobCodeLabel}:</span>{' '}
                                {JobCodeM.of(pr) ?? (
                                  <span className="text-slate-400 italic">No job code</span>
                                )}
                                {JobCodeM.pendingOf(pr) && (
                                  <span className="text-amber-700">
                                    {' '}
                                    &rarr; {JobCodeM.pendingOf(pr)}
                                  </span>
                                )}
                              </span>
                            )}
                          </span>
                          <span>x{showRfqQty ? (pr.rfqQuantity ?? pr.quantity) : pr.quantity}</span>
                        </div>
                      </td>
                      <td className="text-left px-2 py-1.5 align-middle hidden md:table-cell">
                        <Description text={pr.description} />
                      </td>

                      {showJobCode && (
                        <td className="px-2 py-1.5 align-middle text-sm hidden md:table-cell whitespace-nowrap">
                          {JobCodeM.of(pr) ?? <span className="text-slate-400">&mdash;</span>}
                          {JobCodeM.pendingOf(pr) && (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="ml-1 text-amber-700">
                                  &rarr; {JobCodeM.pendingOf(pr)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs p-3 bg-gray-50 border border-gray-300 rounded shadow-sm text-sm text-gray-900 text-center">
                                <p>
                                  Changed to{' '}
                                  <span className="font-medium">{JobCodeM.pendingOf(pr)}</span> in
                                  Gearflow. M5 still has{' '}
                                  <span className="font-medium">{JobCodeM.of(pr)}</span>.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                      )}

                      <td className="px-2 py-1.5 align-middle text-sm hidden md:table-cell">
                        {pr.quantity}
                      </td>
                      {showRfqQty && (
                        <td className="px-2 py-1.5 align-middle text-sm hidden md:table-cell">
                          {pr.rfqQuantity ?? pr.quantity}
                        </td>
                      )}

                      {featureFlags.assetworksApiPartInventory && (
                        <td className="px-2 py-1.5 align-middle text-sm hidden md:table-cell">
                          {!inventoryData ? (
                            <Spinner className="mx-0" size="xs" color="blue" />
                          ) : typeof partInventory === 'undefined' ? (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="text-gray-500">N/A</span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs p-3 bg-gray-50 border border-gray-300 rounded shadow-sm text-sm text-gray-900 text-center space-y-1">
                                <NoMatchingInventory mpn={pr.mpn} location={rfq.shippingLocation} />
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger>{partInventory.availableQuantity}</TooltipTrigger>
                              <TooltipContent className="max-w-xs p-3 bg-gray-50 border border-gray-300 rounded shadow-sm text-sm text-gray-900 text-center space-y-1">
                                {partInventory.availableQuantity ? (
                                  <>
                                    <p>
                                      Location:{' '}
                                      <span className="font-medium">
                                        {partInventory.locationCode}
                                      </span>
                                    </p>
                                    {partInventory.bin && (
                                      <p>
                                        Bin:{' '}
                                        <span className="font-medium">{partInventory.bin}</span>
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <NoMatchingInventory
                                    mpn={pr.mpn}
                                    location={rfq.shippingLocation}
                                  />
                                )}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                      )}

                      {hasFleetioMachine && (
                        <td className="px-2 py-1.5 align-middle text-sm hidden md:table-cell">
                          {!fleetioInventoryInfo ? (
                            <Spinner className="mx-0" size="xs" color="blue" />
                          ) : fleetioInfo?.qty === 'N/A' || fleetioInfo?.tooltip !== '' ? (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="text-gray-500">{fleetioInfo?.qty}</span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs p-3 bg-gray-50 border border-gray-300 rounded shadow-sm text-sm text-gray-900 text-center space-y-1">
                                <p>{fleetioInfo?.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span>{fleetioInfo?.qty}</span>
                          )}
                        </td>
                      )}

                      {rfq.cluePollingEnabled && (
                        <td className="px-2 py-1.5 align-middle text-sm hidden md:table-cell">
                          {!clueInventoryInfo ? (
                            <Spinner className="mx-0" size="xs" color="blue" />
                          ) : (clueInfo?.locations?.length ?? 0) === 1 ? (
                            <span>
                              <span className="font-medium">{clueInfo?.locations[0].name}</span>:{' '}
                              {clueInfo?.locations[0].qty}
                            </span>
                          ) : (clueInfo?.locations?.length ?? 0) > 1 ? (
                            <Tooltip>
                              <TooltipTrigger>
                                <span>
                                  {clueInfo?.qty} (across {clueInfo?.locations.length} locations)
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs p-3 bg-gray-50 border border-gray-300 rounded shadow-sm text-sm text-gray-900 text-left space-y-1">
                                {clueInfo?.locations.map((loc, locIdx) => (
                                  <p
                                    key={
                                      loc.idInventoryLocation != null
                                        ? `id:${loc.idInventoryLocation}`
                                        : `idx:${locIdx}`
                                    }
                                  >
                                    <span className="font-medium">{loc.name}</span>: {loc.qty}
                                  </p>
                                ))}
                              </TooltipContent>
                            </Tooltip>
                          ) : clueInfo?.qty === 'N/A' || clueInfo?.tooltip !== '' ? (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="text-gray-500">{clueInfo?.qty}</span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs p-3 bg-gray-50 border border-gray-300 rounded shadow-sm text-sm text-gray-900 text-center space-y-1">
                                <p>{clueInfo?.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span>{clueInfo?.qty}</span>
                          )}
                        </td>
                      )}

                      {rfq.cluePollingEnabled && (
                        <td className="px-2 py-1.5 align-middle text-sm hidden md:table-cell">
                          {!clueInventoryInfo ? (
                            <Spinner className="mx-0" size="xs" color="blue" />
                          ) : clueInfo?.unitCost ? (
                            <span>{MoneyM.format(clueInfo.unitCost)}</span>
                          ) : (
                            <span className="text-gray-500">N/A</span>
                          )}
                        </td>
                      )}

                      {canEdit && (
                        <td className="align-middle px-2 w-20 text-center">
                          <LinkButton
                            className="text-sm font-medium"
                            onClick={() => setEditingPartRequest(pr)}
                          >
                            <div className="flex items-center gap-1">
                              <PencilIcon className="h-5 w-5" />
                              <span>Edit</span>
                            </div>
                          </LinkButton>
                        </td>
                      )}
                    </tr>
                  )
                })}
            </tbody>
          </table>
          {rfq.partRequests.length === 0 && (
            <p className="text-sm text-gray-500 text-center italic py-6">There are no parts.</p>
          )}

          {showMore !== 'none' && (
            <div className="text-sm !text-gray-700 border-t px-2 py-2">
              <LinkButton
                onClick={
                  onShowMoreChange
                    ? () =>
                        onShowMoreChange(
                          showMore === ShowMore.Hidden ? ShowMore.Visible : ShowMore.Hidden
                        )
                    : undefined
                }
              >
                {showMore === ShowMore.Hidden ? '...Show more' : 'Show less'}
              </LinkButton>
            </div>
          )}
          {canAdd && (
            <div className="p-2 border-t">
              <LinkButton className="text-sm font-medium" onClick={addingToggler.on}>
                <div className="flex items-center gap-1">
                  <PlusCircleIcon className="w-5 h-5" />
                  <span>Add part</span>
                </div>
              </LinkButton>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default PartRequestsTableV2
